import { z } from 'zod';
import { environmentIdSchema, httpMethodSchema, isoTimestampSchema, outcomeSchema } from './common.js';
/**
 * RocketPBX exposes two unrelated HTTP surfaces and they fail independently, so
 * the platform treats them as first-class peers rather than one blurred list.
 *
 * - `admin`   provisioning API, authenticated with a tenant-scoped `X-API-Key`
 * - `gateway` the API the Salesforce package calls, authenticated with an OAuth
 *             client-credentials tenant token
 */
export const apiSurfaceSchema = z.enum(['admin', 'gateway']);
export const API_SURFACE_LABELS = {
    admin: 'PBX Admin API',
    gateway: 'Salesforce Gateway API',
};
export const authModeSchema = z.enum([
    'none',
    'api-key',
    'tenant-token',
    'client-credentials',
    'hmac-signed',
]);
/**
 * How safely an endpoint can be exercised.
 *
 * - `live`         read-only; call it for real and require a success status
 * - `reachability` mutating; call it with a deliberately incomplete payload so a
 *                  4xx validation response proves the route and credentials are
 *                  healthy without changing any data
 * - `manual`       cannot be probed automatically; surfaced as informational
 */
export const probeModeSchema = z.enum(['live', 'reachability', 'manual']);
export const probeSpecSchema = z.object({
    mode: probeModeSchema,
    /** Override the verb when probing, for example HEAD instead of DELETE. */
    method: httpMethodSchema.optional(),
    /** Context keys that must be resolved before this probe can run. */
    requires: z.array(z.string()).default([]),
    /** Static query string added to the probe request. */
    query: z.record(z.string()).optional(),
    /** Body sent for reachability probes; intentionally invalid where useful. */
    body: z.unknown().optional(),
    /** Statuses that count as healthy, overriding the mode's defaults. */
    healthyStatuses: z.array(z.number().int()).optional(),
    /** Why this endpoint cannot be probed, required when mode is `manual`. */
    manualReason: z.string().optional(),
});
export const apiEndpointSchema = z.object({
    id: z.string(),
    surface: apiSurfaceSchema,
    group: z.string(),
    method: httpMethodSchema,
    /** Path template; `{placeholders}` are filled from run context. */
    path: z.string(),
    title: z.string(),
    /** One sentence on what the endpoint does. */
    purpose: z.string(),
    /**
     * Plain-English consequence of this endpoint being down, shown to people
     * outside engineering so a red card means something concrete.
     */
    impact: z.string(),
    auth: authModeSchema,
    probe: probeSpecSchema,
    /** Where in the reference codebases this endpoint was traced from. */
    tracedFrom: z.string().optional(),
    /** Marked when the endpoint exists in a client but is no longer called. */
    deprecated: z.boolean().default(false),
});
export const probeResultSchema = z.object({
    endpointId: z.string(),
    outcome: outcomeSchema,
    requestUrl: z.string().optional(),
    requestMethod: httpMethodSchema.optional(),
    status: z.number().int().optional(),
    statusText: z.string().optional(),
    latencyMs: z.number().int().nonnegative().optional(),
    /** Truncated response body, enough to diagnose without dumping payloads. */
    responseSnippet: z.string().optional(),
    reason: z.string().optional(),
    checkedAt: isoTimestampSchema,
});
export const healthReportSchema = z.object({
    id: z.string(),
    environment: environmentIdSchema,
    tenantCode: z.string().optional(),
    startedAt: isoTimestampSchema,
    endedAt: isoTimestampSchema.optional(),
    outcome: outcomeSchema,
    surfaces: z.array(apiSurfaceSchema),
    results: z.array(probeResultSchema),
    totals: z.object({
        total: z.number().int().nonnegative(),
        passed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        warning: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
    }),
});
export const healthRunRequestSchema = z.object({
    environment: environmentIdSchema,
    surfaces: z.array(apiSurfaceSchema).min(1).default(['admin', 'gateway']),
    /** Restrict to specific groups; empty means every group. */
    groups: z.array(z.string()).default([]),
    tenantCode: z.string().optional(),
    /** Skip endpoints whose probe mode is `reachability`. */
    readOnlyOnly: z.boolean().default(false),
});
//# sourceMappingURL=api-health.js.map